import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { gqlClient } from "@/lib/graphql";

export type ConflictResolution = "mine" | "repo" | "merge";

const QUERY = gql`
  query Conflicts($slug: String!) {
    book(slug: $slug) {
      id
      conflictCount
      conflictSegments {
        id
        chapterId
        chapterTitle
        externalId
        targetText
        conflictRepoText
      }
      guideConflicts {
        key
        label
        localText
        repoText
      }
    }
  }
`;

const RESOLVE_ONE = gql`
  mutation ResolveSegmentConflict(
    $segmentId: ID!
    $resolution: String!
    $mergedText: String
  ) {
    resolveSegmentConflict(
      input: {
        segmentId: $segmentId
        resolution: $resolution
        mergedText: $mergedText
      }
    ) {
      segment {
        id
        conflicted
      }
      errors
    }
  }
`;

const RESOLVE_GUIDE = gql`
  mutation ResolveGuideConflict(
    $bookId: ID!
    $key: String!
    $resolution: String!
    $mergedText: String
  ) {
    resolveGuideConflict(
      input: {
        bookId: $bookId
        key: $key
        resolution: $resolution
        mergedText: $mergedText
      }
    ) {
      book {
        id
        conflictCount
      }
      errors
    }
  }
`;

const RESOLVE_ALL = gql`
  mutation ResolveAllConflicts($bookId: ID!, $resolution: String!) {
    resolveAllConflicts(input: { bookId: $bookId, resolution: $resolution }) {
      book {
        id
        conflictCount
      }
      errors
    }
  }
`;

export interface ConflictSegment {
  id: string;
  chapterId: string | null;
  chapterTitle: string | null;
  externalId: string;
  // "mine" — the local wording we kept.
  targetText: string | null;
  // "repo" — the competing text the repo holds.
  conflictRepoText: string | null;
}

export interface GuideConflict {
  // Guide identifier, e.g. "glossary" — pass to resolveGuideConflict.
  key: string;
  // Human title, e.g. "Glossary".
  label: string;
  // "mine" — the local value we kept.
  localText: string | null;
  // "repo" — the competing value the repo holds.
  repoText: string | null;
}

export interface ConflictData {
  id: string;
  conflictCount: number;
  conflictSegments: ConflictSegment[];
  guideConflicts: GuideConflict[];
}

export function useConflicts(slug: string, enabled = true) {
  return useQuery({
    queryKey: ["conflicts", slug],
    enabled,
    queryFn: async () =>
      (await gqlClient.request<{ book: ConflictData }>(QUERY, { slug })).book,
  });
}

// Invalidate the conflict queue, the header count, and the open chapter (its
// target_text may have changed) whenever a conflict is resolved.
function invalidate(qc: ReturnType<typeof useQueryClient>, slug: string) {
  qc.invalidateQueries({ queryKey: ["conflicts", slug] });
  qc.invalidateQueries({ queryKey: ["sync-status", slug] });
  qc.invalidateQueries({ queryKey: ["chapter"] });
}

// Resolve one conflict: keep mine, take repo, or store a hand-merge.
export function useResolveConflict(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      segmentId,
      resolution,
      mergedText,
    }: {
      segmentId: string;
      resolution: ConflictResolution;
      mergedText?: string;
    }) => {
      const res = await gqlClient.request<{
        resolveSegmentConflict: {
          segment: { id: string } | null;
          errors: string[];
        };
      }>(RESOLVE_ONE, {
        segmentId,
        resolution,
        mergedText: mergedText ?? null,
      });
      if (res.resolveSegmentConflict.errors.length)
        throw new Error(res.resolveSegmentConflict.errors.join(", "));
      return res.resolveSegmentConflict.segment;
    },
    onSuccess: () => invalidate(qc, slug),
  });
}

// Resolve one guide conflict: keep mine, take repo, or store a hand-merge.
export function useResolveGuideConflict(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bookId,
      key,
      resolution,
      mergedText,
    }: {
      bookId: string;
      key: string;
      resolution: ConflictResolution;
      mergedText?: string;
    }) => {
      const res = await gqlClient.request<{
        resolveGuideConflict: { book: { id: string } | null; errors: string[] };
      }>(RESOLVE_GUIDE, {
        bookId,
        key,
        resolution,
        mergedText: mergedText ?? null,
      });
      if (res.resolveGuideConflict.errors.length)
        throw new Error(res.resolveGuideConflict.errors.join(", "));
      return res.resolveGuideConflict.book;
    },
    onSuccess: () => invalidate(qc, slug),
  });
}

// Bulk shortcut: keep every local wording, or take the repo's everywhere.
export function useResolveAllConflicts(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      bookId,
      resolution,
    }: {
      bookId: string;
      resolution: "mine" | "repo";
    }) => {
      const res = await gqlClient.request<{
        resolveAllConflicts: { book: { id: string } | null; errors: string[] };
      }>(RESOLVE_ALL, { bookId, resolution });
      if (res.resolveAllConflicts.errors.length)
        throw new Error(res.resolveAllConflicts.errors.join(", "));
      return res.resolveAllConflicts.book;
    },
    onSuccess: () => invalidate(qc, slug),
  });
}
